import { Ionicons } from "@expo/vector-icons";
import { ActivityAction, ActivityLogModel } from "common247";

/**
 * Secondary or related entity extracted from activity log details metadata.
 */
export interface ISecondaryEntity {
  type: string;
  id: string;
  label?: string;
  thumbnailUrl?: string;
  date?: string;
  recipientName?: string;
}

/**
 * Returns user-friendly presentation details for an activity action.
 */
export function getActionBadgeConfig(action: ActivityAction): {
  label: string;
  bg: string;
  text: string;
  icon: keyof typeof Ionicons.glyphMap;
} {
  switch (action) {
    case "create":
      return { label: "Created", bg: "#e6f4ea", text: "#137333", icon: "add-circle-outline" };
    case "update":
      return { label: "Updated", bg: "#e8f0fe", text: "#1a73e8", icon: "pencil-outline" };
    case "delete":
      return { label: "Deleted", bg: "#fce8e6", text: "#c5221f", icon: "trash-outline" };
    case "logon":
      return { label: "Logon", bg: "#e0f2fe", text: "#0369a1", icon: "log-in-outline" };
    case "signout":
      return { label: "Sign out", bg: "#f1f5f9", text: "#475569", icon: "log-out-outline" };
    case "signup":
      return { label: "Sign up", bg: "#ecfdf5", text: "#047857", icon: "sparkles-outline" };
    case "terminate_account":
      return { label: "Terminated", bg: "#fee2e2", text: "#b91c1c", icon: "close-circle-outline" };
    case "invite_contact":
      return { label: "Invited", bg: "#e0f2fe", text: "#0369a1", icon: "mail-outline" };
    default:
      return { label: action, bg: "#f3f4f6", text: "#374151", icon: "time-outline" };
  }
}

/**
 * Formats epoch millisecond timestamp into a human-friendly relative or exact time string.
 */
export function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return "";
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;

  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Extracts secondary linked entities from activity details metadata (e.g. attached memory, media, contact, group, share).
 */
export function extractSecondaryEntities(activity: ActivityLogModel): ISecondaryEntity[] {
  const secondaryLinks: ISecondaryEntity[] = [];
  if (activity.details && typeof activity.details === "object") {
    if (activity.details.memoryId && activity.details.memoryId !== activity.targetId) {
      secondaryLinks.push({
        type: "memory",
        id: String(activity.details.memoryId),
        label: activity.details.memoryTitle || `Memory #${activity.details.memoryId}`,
        thumbnailUrl: activity.details.memoryThumbnailUrl || activity.details.thumbnailUrl,
        date: activity.details.memoryDate,
      });
    }
    if (activity.details.mediaId && activity.details.mediaId !== activity.targetId) {
      secondaryLinks.push({
        type: "media",
        id: String(activity.details.mediaId),
        label: activity.details.mediaTitle || `Media #${activity.details.mediaId}`,
        thumbnailUrl:
          activity.details.mediaThumbnailUrl ||
          activity.details.thumbnailUrl ||
          activity.details.downloadUrl ||
          activity.details.url,
      });
    }
    if (activity.details.contactId && activity.details.contactId !== activity.targetId) {
      secondaryLinks.push({
        type: "contact",
        id: String(activity.details.contactId),
        label: activity.details.contactName || `Contact #${activity.details.contactId}`,
        thumbnailUrl: activity.details.contactAvatarUrl || activity.details.avatarUrl,
      });
    }
    if (activity.details.groupId && activity.details.groupId !== activity.targetId) {
      secondaryLinks.push({
        type: "group",
        id: String(activity.details.groupId),
        label: activity.details.groupName || `Group #${activity.details.groupId}`,
        thumbnailUrl: activity.details.groupThumbnailUrl,
      });
    }
    if (activity.details.shareId && activity.details.shareId !== activity.targetId) {
      secondaryLinks.push({
        type: "share",
        id: String(activity.details.shareId),
        label: activity.details.shareTitle || `Share #${activity.details.shareId}`,
        recipientName: activity.details.recipientName || activity.details.shareRecipient,
      });
    }
  }
  return secondaryLinks;
}
